import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AppError } from '@rms/sdk/errors';
import { PageableParam, Paginated } from '@rms/sdk/models';
import { Filter } from '@rms/ui/filters';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';

export type PageLoader<T> = (pageable: PageableParam, filters: Filter[]) => Observable<Paginated<T>>;

// Newest first; the API has no default order, so pages could otherwise overlap
const NEWEST_FIRST_SORT = 'createdAt,desc';

interface PagedListQuery {
  page: number;
  filters: Filter[];
}

const pageOf = (params: ParamMap): number => {
  const page = Number(params.get('page'));

  return Number.isInteger(page) && page > 0 ? page : 0;
};

// Page 0 is the bare URL, so the first load adds no ?page=0
const pageParam = (page: number): string | null => (page > 0 ? String(page) : null);

/**
 * State of a paged, filtered table whose page lives in `?page=`, so Back and Forward move it.
 * Create it in an injection context (a component field), because it follows the component's route.
 */
export class PagedList<T> {
  public readonly error$ = new BehaviorSubject<string | null>(null);
  public readonly loading$ = new BehaviorSubject<boolean>(false);
  public readonly pageSize = 20;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly query$ = new BehaviorSubject<PagedListQuery>({
    page: pageOf(this.route.snapshot.queryParamMap),
    filters: [],
  });

  public filtered$ = this.query$.pipe(map(({ filters }) => filters.length > 0));

  public page$: Observable<Paginated<T> | null> = this.query$.pipe(
    tap(() => {
      this.error$.next(null);
      this.loading$.next(true);
    }),
    switchMap(({ page, filters }) =>
      this.load({ page, size: this.pageSize, sort: [NEWEST_FIRST_SORT] }, filters).pipe(
        tap(({ total_pages }) => {
          const lastPage = Math.max(total_pages - 1, 0);

          if (page > lastPage) {
            this.query$.next({ page: lastPage, filters });
            this.writePage(lastPage, true);
          }
        }),
        catchError((error: AppError) => {
          this.error$.next(error.message);

          return of(null);
        }),
        tap(() => this.loading$.next(false))
      )
    )
  );

  constructor(private readonly load: PageLoader<T>) {
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const page = pageOf(params);

      if (params.get('page') !== pageParam(page)) {
        this.writePage(page, true);
      }
      if (page !== this.query$.value.page) {
        this.query$.next({ ...this.query$.value, page });
      }
    });
  }

  public setPage(page: number): void {
    this.query$.next({ ...this.query$.value, page });
    this.writePage(page);
  }

  public applyFilters(filters: Filter[]): void {
    this.query$.next({ page: 0, filters });
    this.writePage(0);
  }

  public reload(): void {
    this.query$.next(this.query$.value);
  }

  private writePage(page: number, replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: pageParam(page) },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }
}
